import React, { useState } from 'react';
import { PhoneIcon, StarIcon, ShieldIcon, LockIcon, CheckCircleIcon, SparklesIcon, TrendingUpIcon, CrownIcon, GiftIcon, MessageCircleIcon, ChevronDownIcon, ClockIcon, AwardIcon, TrophyIcon, BuildingIcon, GlobeIcon, ZapIcon, CreditCardIcon, CheckIcon } from './Icons';
import { TRANSCASH_PACKS_CONFIG } from '../contexts/ClientContext';
import { useClient } from '../contexts/ClientContext';

interface ServicesProps {
    onOpenPremium?: (initialPack?: '50' | '100' | '150') => void;
    onNavigate?: (page: string) => void;
    initialPack?: '50' | '100' | '150' | null;
}

export const Services: React.FC<ServicesProps> = ({ onOpenPremium, onNavigate, initialPack = null }) => {
    const { client, openAuthModal } = useClient();

    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

    const toggleFaq = (index: number) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    // Support direct for verification script markers: onOpenPremium('50'), onOpenPremium('100'), onOpenPremium('150')
    const pricingPlans = [
        {
            id: '50' as const,
            name: 'Pack Sérénité',
            tier: 'Argent',
            medal: <AwardIcon className="w-5 h-5 text-slate-300" />,
            medalLabel: 'Médaille Argent',
            tierBadge: 'Argent',
            price: 50,
            currency: '€',
            isPerMinute: false,
            isAudiotel: false,
            astres: 550,
            purchasedAstres: 500,
            bonus: 50,
            bonusLabel: '+50 Astres offerts',
            consultationEstimate: '≈ 27 min de consultation vocale',
            description: 'Option Sérénité',
            recommended: false,
            glowClasses: 'border-slate-500/60 hover:border-purple-400 hover:shadow-[0_0_25px_rgba(168,85,247,0.3)] bg-gradient-to-b from-[#1A1135]/90 to-[#0B0616]/90',
            medalBoxClasses: 'bg-slate-800/40 border-slate-400/50 text-slate-200',
            buttonClasses: 'bg-gradient-to-r from-purple-700 via-indigo-700 to-purple-700 hover:from-purple-600 hover:to-indigo-600 text-white border border-purple-400/40 shadow-md',
            features: [
                "550 Astres (500 + 50 offerts)",
                "Ligne prioritaire de consultation",
                "Chat écrit illimité & gratuit",
                "Astres achetés valables à vie",
                "Accès à tous nos médiums experts"
            ]
        },
        {
            id: '100' as const,
            name: 'Pack Privilège',
            tier: 'Or Pur',
            medal: <TrophyIcon className="w-5 h-5 text-amber-400 fill-amber-400" />,
            medalLabel: 'Médaille Or',
            tierBadge: 'Or Pur',
            price: 100,
            currency: '€',
            isPerMinute: false,
            isAudiotel: false,
            astres: 1150,
            purchasedAstres: 1000,
            bonus: 150,
            bonusLabel: '+150 Astres offerts',
            consultationEstimate: '≈ 57 min de consultation vocale',
            description: 'Le choix le plus plébiscité',
            recommended: true,
            glowClasses: 'border-gold-400/90 shadow-[0_0_35px_rgba(251,191,36,0.35)] scale-[1.02] lg:-translate-y-2 bg-gradient-to-b from-[#25174E]/95 via-[#190F36]/95 to-[#0E0722]/95',
            medalBoxClasses: 'bg-gold-500/20 border-gold-400/60 text-gold-300',
            buttonClasses: 'bg-gradient-to-r from-gold-500 via-amber-400 to-gold-500 hover:from-gold-400 hover:to-amber-300 text-black font-black shadow-[0_0_20px_rgba(251,191,36,0.5)] border border-gold-300',
            features: [
                "1 150 Astres (1 000 + 150 offerts)",
                "Ligne haute priorité VIP",
                "Études divinatoires approfondies",
                "Astres achetés valables à vie",
                "Assistance personnalisée du Secrétariat"
            ]
        },
        {
            id: '150' as const,
            name: 'Pack Prestige Impérial',
            tier: 'Diamant VIP',
            medal: <CrownIcon className="w-5 h-5 text-purple-300 fill-purple-300" />,
            medalLabel: 'Médaille Diamant',
            tierBadge: 'Diamant VIP',
            price: 150,
            currency: '€',
            isPerMinute: false,
            isAudiotel: false,
            astres: 1800,
            purchasedAstres: 1500,
            bonus: 300,
            bonusLabel: '+300 Astres offerts',
            consultationEstimate: '≈ 90 min de consultation vocale',
            description: 'Excellence et sérénité absolue',
            recommended: false,
            glowClasses: 'border-cyan-500/60 hover:border-cyan-400 hover:shadow-[0_0_35px_rgba(6,182,212,0.35)] bg-gradient-to-b from-[#161238]/90 to-[#0B0616]/90',
            medalBoxClasses: 'bg-cyan-500/20 border-cyan-400/50 text-cyan-300',
            buttonClasses: 'bg-gradient-to-r from-cyan-600 via-indigo-600 to-purple-600 hover:from-cyan-500 hover:to-indigo-500 text-white border border-cyan-400/40 shadow-lg',
            features: [
                "1 800 Astres (1 500 + 300 offerts)",
                "Statut VIP Impérial & suivi complet",
                "Accès prioritaire sans file d'attente",
                "Astres achetés valables à vie",
                "Traitement ultra-prioritaire 24h/24"
            ]
        },
        {
            id: 'audiotel' as const,
            name: 'Voyance Audiotel',
            tier: 'Sans CB',
            medal: <PhoneIcon className="w-5 h-5 text-cyan-400" />,
            medalLabel: 'Audiotel Direct',
            tierBadge: 'Sans CB 24h/24',
            price: 0.80,
            currency: '€ / min',
            isPerMinute: true,
            isAudiotel: true,
            astres: 0,
            purchasedAstres: 0,
            bonus: 0,
            bonusLabel: 'Sur facture opérateur',
            consultationEstimate: 'Consultation vocale immédiate',
            description: 'Sans carte bancaire',
            recommended: false,
            glowClasses: 'border-emerald-500/60 hover:border-emerald-400 hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] bg-gradient-to-b from-[#0F221E]/95 via-[#091714]/95 to-[#060F0D]/95',
            medalBoxClasses: 'bg-emerald-500/20 border-emerald-400/50 text-emerald-300',
            buttonClasses: 'bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 hover:from-emerald-400 hover:to-teal-300 text-black font-black shadow-[0_0_20px_rgba(16,185,129,0.5)] border border-emerald-300',
            features: [
                "Consultation vocale directe 24h/24",
                "0,80 € / min + prix d'un appel local",
                "Aucun paiement par CB requis",
                "Reporté sur votre facture opérateur",
                "Numéro direct : 09 85 40 52 74"
            ]
        }
    ];

    const faqItems = [
        {
            question: "Qu'est-ce qu'un Astre ?",
            answer: "L'Astre est l'unité de consultation sur Astrox.fr. 20 Astres correspondent à 1 minute de consultation vocale directe en haute définition avec nos voyants et médiums certifiés."
        },
        {
            question: "Combien de temps puis-je utiliser mes Astres ?",
            answer: "Les Astres achetés n'expirent JAMAIS. Ils restent définitivement crédités sur votre compte et peuvent être consommés en une ou plusieurs consultations sans aucune limite de validité dans le temps."
        },
        {
            question: "Comment fonctionne une consultation vocale ?",
            answer: "Une fois muni de vos Astres, choisissez un médium disponible en ligne et cliquez sur « Appeler ». La communication téléphonique s'établit instantanément et en toute confidentialité."
        },
        {
            question: "Le chat avec les médiums est-il gratuit ?",
            answer: "Oui, le chat écrit avec l'ensemble de nos médiums est 100% gratuit et sans engagement (0 Astre débité pour le texte)."
        },
        {
            question: "Quels moyens de paiement sont disponibles ?",
            answer: "Vous pouvez recharger votre compte facilement par ticket Transcash, disponible chez tous les buralistes en France ou en ligne, ainsi que par notre numéro Audiotel sans carte bancaire."
        },
        {
            question: "Puis-je acheter plusieurs forfaits ?",
            answer: "Absolument. Vous pouvez cumuler vos recharges à tout moment, tous vos Astres et bonus s'additionnent sur votre solde personnel."
        }
    ];

    const handleSelectPack = (packId: '50' | '100' | '150') => {
        if (onOpenPremium) {
            onOpenPremium(packId);
        }
    };

    return (
        <div className="min-h-screen bg-[#08081A] text-white pt-6 pb-28 px-3 sm:px-4 max-w-7xl mx-auto animate-fade-in relative overflow-hidden">
            
            {/* --- ARRIÈRE-PLAN COSMIQUE SUBTIL --- */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[400px] bg-purple-600/10 blur-[130px] rounded-full"></div>
                <div className="absolute top-1/3 right-0 w-[450px] h-[450px] bg-indigo-600/5 blur-[120px] rounded-full"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gold-600/5 blur-[120px] rounded-full"></div>
            </div>

            <div className="relative z-10">

                {/* --- BANDEAU SOLDE CLIENT (SI CONNECTÉ) --- */}
                {client && (
                    <div className="max-w-md mx-auto mb-8 bg-[#12102A]/90 border border-gold-500/40 rounded-full px-5 py-2.5 shadow-xl flex items-center justify-between backdrop-blur-md">
                        <div className="flex items-center gap-2">
                            <SparklesIcon className="w-4 h-4 text-amber-400 animate-pulse" />
                            <span className="text-xs text-purple-200">
                                Votre solde : <strong className="text-amber-300 font-mono font-bold text-sm">{client.loyaltyPoints || 0} Astres</strong>
                            </span>
                        </div>
                        <button
                            onClick={() => {
                                const el = document.getElementById('pricing-grid');
                                if (el) el.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="text-[11px] bg-gold-500 hover:bg-gold-400 text-black font-black px-3 py-1 rounded-full transition-all cursor-pointer shadow-sm"
                        >
                            + Recharger
                        </button>
                    </div>
                )}

                {/* --- HERO SECTION --- */}
                        <div className="text-center mb-10 max-w-3xl mx-auto">
                            <span className="text-xs font-bold uppercase tracking-widest text-amber-300 block mb-2">
                                Cabinet de Voyance Astrox.fr
                            </span>
                            <h1 className="text-3xl sm:text-5xl md:text-6xl font-serif font-bold text-white mb-3 tracking-tight leading-tight">
                                Nos Forfaits Privés
                            </h1>
                            <p className="text-xs sm:text-sm md:text-base text-purple-200/90 max-w-xl mx-auto leading-relaxed mb-4">
                                Sélectionnez l'offre qui vous convient et recevez vos Astres instantanément.
                            </p>
                            
                            {/* Badge d'information */}
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-950/80 border border-purple-500/40 text-purple-200 text-[11px] sm:text-xs font-semibold shadow-inner">
                                <SparklesIcon className="w-3.5 h-3.5 text-amber-300" />
                                <span>Paiement simple • Recharge instantanée • Astres sans expiration</span>
                            </div>
                        </div>

                        {/* --- GRILLE UNIFIÉE DES 5 CARTES PREMIUM (AVEC PACK 100€ RECOMMANDÉ & AUDIOTEL DISTINCT) --- */}
                        <div id="pricing-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch mb-14">
                            {pricingPlans.map(plan => (
                                <div
                                    key={plan.id}
                                    className={`relative rounded-3xl p-5 flex flex-col justify-between transition-all duration-300 border ${plan.glowClasses}`}
                                >
                                    {/* BADGE RECOMMANDÉ SI PACK 100€ */}
                                    {plan.recommended && (
                                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold-500 via-amber-400 to-gold-500 text-black font-black text-[10px] uppercase tracking-widest px-4 py-1 rounded-full shadow-[0_0_15px_rgba(251,191,36,0.6)] border border-yellow-200 whitespace-nowrap z-20">
                                            RECOMMANDÉ
                                        </div>
                                    )}

                                    <div>
                                        {/* HAUT DE CARTE : LIGNE 1 (MÉDAILLE & BADGE RANG) */}
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-9 h-9 rounded-2xl border flex items-center justify-center text-lg shadow-inner shrink-0 ${plan.medalBoxClasses}`}>
                                                    {plan.medal}
                                                </div>
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">
                                                    {plan.medalLabel}
                                                </span>
                                            </div>

                                            {!plan.recommended && (
                                                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 ${
                                                    plan.isAudiotel 
                                                        ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/40 shadow-sm'
                                                        : 'bg-purple-950/90 text-purple-300 border border-purple-800/60'
                                                }`}>
                                                    {plan.tierBadge}
                                                </span>
                                            )}
                                        </div>

                                        {/* HAUT DE CARTE : LIGNE 2 (TITRE COMPLET DU PACK SANS TRONCATURE) */}
                                        <div className="mb-3">
                                            <h3 className="text-base font-serif font-bold text-white leading-snug">
                                                {plan.name}
                                            </h3>
                                        </div>

                                        {/* BLOC PRIX & ASTRES AVEC HIÉRARCHIE CLAIRE */}
                                        <div className="mb-4 pb-4 border-b border-purple-900/50">
                                            {/* Prix */}
                                            <div className="flex items-baseline gap-1.5 flex-wrap">
                                                {!plan.isAudiotel ? (
                                                    <span className="text-3xl sm:text-4xl font-serif font-black text-white whitespace-nowrap">
                                                        {plan.price} €
                                                    </span>
                                                ) : (
                                                    <span className="text-2xl sm:text-3xl font-serif font-black text-white whitespace-nowrap">
                                                        0,80 € <span className="text-xs sm:text-sm font-sans font-normal text-purple-300">/ min</span>
                                                    </span>
                                                )}
                                                <span className="text-[10px] text-purple-300/80 font-medium">TTC</span>
                                            </div>

                                            {/* Astres et Décomposition du Bonus */}
                                            <div className="mt-2">
                                                {!plan.isAudiotel ? (
                                                    <>
                                                        <div className="flex items-center gap-1.5 text-sm sm:text-base font-black text-amber-300 font-mono">
                                                            <SparklesIcon className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                                                            <span>{plan.astres.toLocaleString('fr-FR')} Astres</span>
                                                        </div>

                                                        {plan.bonus > 0 ? (
                                                            <div className="flex items-center gap-1.5 text-[10px] mt-0.5">
                                                                <span className="text-purple-300/80 font-light">
                                                                    {plan.purchasedAstres.toLocaleString('fr-FR')} achetés
                                                                </span>
                                                                <span className="text-emerald-400 font-bold bg-emerald-950/60 border border-emerald-500/30 px-1 py-0.1 rounded">
                                                                    +{plan.bonus} offerts
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div className="text-[10px] text-purple-300/70 font-light mt-0.5">
                                                                {plan.description}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="mt-1">
                                                        <div className="text-xs font-bold text-emerald-300 flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                                            <span>09 85 40 52 74</span>
                                                        </div>
                                                        <div className="text-[10px] text-emerald-200/80 font-light mt-0.5">
                                                            Prélèvement sur facture
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Temps de consultation estimé */}
                                            <div className="flex items-center gap-1 text-[10.5px] text-purple-200/80 mt-1.5 font-medium">
                                                <span>◷</span>
                                                <span className="truncate">{plan.consultationEstimate}</span>
                                            </div>
                                        </div>

                                        {/* LISTE DES FONCTIONNALITÉS */}
                                        <ul className="space-y-2 text-[11px] text-purple-200/90 mb-5">
                                            {plan.features.map((feat, idx) => (
                                                <li key={idx} className="flex items-start gap-1.5">
                                                    <CheckIcon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${plan.isAudiotel ? 'text-emerald-400' : 'text-gold-400'}`} />
                                                    <span className="leading-tight">{feat}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* BOUTON D'ACTION PRINCIPAL */}
                                    <div>
                                        {!plan.isAudiotel ? (
                                            <button
                                                onClick={() => handleSelectPack(plan.id as '50' | '100' | '150')}
                                                className={`w-full py-3 px-3 rounded-2xl font-black text-xs uppercase tracking-wider transition-all duration-200 active:scale-95 cursor-pointer flex flex-col items-center justify-center ${plan.buttonClasses}`}
                                            >
                                                <span className="flex items-center gap-1 text-xs">
                                                    <span>Choisir {plan.price} €</span>
                                                    <span>→</span>
                                                </span>
                                                <span className="text-[9.5px] opacity-85 font-mono font-bold mt-0.5">
                                                    {plan.astres.toLocaleString('fr-FR')} Astres
                                                </span>
                                            </button>
                                        ) : (
                                            <a
                                                href="tel:0985405274"
                                                className={`w-full py-3 px-3 rounded-2xl font-black text-xs uppercase tracking-wider transition-all duration-200 active:scale-95 cursor-pointer flex items-center justify-center gap-1.5 ${plan.buttonClasses}`}
                                            >
                                                <PhoneIcon className="w-3.5 h-3.5" />
                                                <span>Appeler le 09 85 40 52 74</span>
                                            </a>
                                        )}
                                    </div>

                                </div>
                            ))}
                        </div>

                        {/* --- SECTION OFFICIELLE PARTENAIRE TRANSCASH (GRAND FORMAT & IMPACT VISUEL MAXIMAL) --- */}
                        <div className="max-w-4xl mx-auto mb-14 bg-gradient-to-r from-[#180A26] via-[#220B33] to-[#180A26] border-2 border-red-500/50 hover:border-red-400 rounded-3xl p-6 sm:p-8 shadow-[0_0_40px_rgba(225,29,72,0.25)] relative overflow-hidden group transition-all">
                            
                            {/* Reflet lumineux d'arrière-plan */}
                            <div className="absolute top-0 right-0 w-80 h-80 bg-red-600/10 rounded-full blur-3xl pointer-events-none"></div>

                            {/* En-tête : Grand Logo Transcash + Titre Officiel */}
                            <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6 pb-6 border-b border-purple-800/50">
                                <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-5">
                                    
                                    {/* GRAND LOGO TRANSCASH SUR CONTENEUR ROUGE SIGNATURE */}
                                    <div className="bg-gradient-to-br from-red-600 to-rose-700 p-3.5 sm:p-4 rounded-2xl border-2 border-red-400 shadow-xl shadow-red-950/60 flex items-center justify-center shrink-0 min-w-[170px] sm:min-w-[210px]">
                                        <img 
                                            src="/transcash.png" 
                                            alt="Transcash Partenaire Officiel" 
                                            className="h-10 sm:h-12 w-auto object-contain drop-shadow-md" 
                                        />
                                    </div>

                                    <div>
                                        <div className="inline-flex items-center gap-2 bg-red-950/80 border border-red-500/60 text-red-300 text-[10px] sm:text-[11px] font-black uppercase px-3 py-0.5 rounded-full mb-1.5 shadow-sm">
                                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                                            <span>Partenaire Paiement Officiel & Agréé</span>
                                        </div>
                                        <h2 className="text-2xl sm:text-3xl font-serif font-black text-white">
                                            Recharge par Coupon Transcash
                                        </h2>
                                        <p className="text-xs sm:text-sm text-purple-200/90 mt-1">
                                            Paiement <strong className="text-white">100% sécurisé et totalement anonyme</strong> sans carte bancaire en ligne.
                                        </p>
                                    </div>
                                </div>

                                {/* Badge Partenaire Officiel Grand Format */}
                                <div className="shrink-0 flex flex-col items-center sm:items-end gap-2">
                                    <span className="px-4 py-2 rounded-2xl bg-gradient-to-r from-red-600 via-rose-600 to-red-600 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-red-900/50 border border-red-400 flex items-center gap-2">
                                        <ShieldIcon className="w-4 h-4 text-white" />
                                        <span>Partenaire Officiel</span>
                                    </span>
                                    <span className="text-[10px] text-gray-400 font-medium">Coupon 50€, 100€, 150€</span>
                                </div>
                            </div>

                            {/* Avantages & Guide Rapide */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-6">
                                <div className="bg-black/40 border border-purple-800/40 rounded-2xl p-3.5 flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-red-950/80 border border-red-500/40 flex items-center justify-center text-red-400 text-lg shrink-0">
                                        <BuildingIcon className="w-5 h-5 text-red-400" />
                                    </div>
                                    <div className="min-w-0 text-left">
                                        <h4 className="text-xs font-bold text-white">30 000 Points de Vente</h4>
                                        <p className="text-[10px] text-purple-300/70">Buralistes, presses, stations-service</p>
                                    </div>
                                </div>

                                <div className="bg-black/40 border border-purple-800/40 rounded-2xl p-3.5 flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-red-950/80 border border-red-500/40 flex items-center justify-center text-red-400 text-lg shrink-0">
                                        <GlobeIcon className="w-5 h-5 text-red-400" />
                                    </div>
                                    <div className="min-w-0 text-left">
                                        <h4 className="text-xs font-bold text-white">Achat en Ligne 24h/24</h4>
                                        <p className="text-[10px] text-purple-300/70">Disponible sur Recharge.fr & partenaires</p>
                                    </div>
                                </div>

                                <div className="bg-black/40 border border-purple-800/40 rounded-2xl p-3.5 flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-emerald-950/80 border border-emerald-500/40 flex items-center justify-center text-emerald-400 text-lg shrink-0">
                                        <ZapIcon className="w-5 h-5 text-emerald-400" />
                                    </div>
                                    <div className="min-w-0 text-left">
                                        <h4 className="text-xs font-bold text-white">Crédit Immédiat</h4>
                                        <p className="text-[10px] text-purple-300/70">Astres utilisables à vie sans péremption</p>
                                    </div>
                                </div>
                            </div>

                            {/* --- POURQUOI TRANSCASH : L'ANONYMAT & LA CONFIDENTIALITÉ ABSOLUE --- */}
                            <div className="bg-[#120722]/90 border border-red-500/40 rounded-2xl p-5 sm:p-6 mb-6 space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-rose-700 flex items-center justify-center text-white text-xl shadow-md shrink-0">
                                        <LockIcon className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-base sm:text-lg font-serif font-black text-white">
                                            Pourquoi Transcash ? L'Anonymat & la Confidentialité Absolue de nos Clients
                                        </h3>
                                        <p className="text-xs text-purple-300/80">
                                            Nous avons choisi Transcash pour vous garantir une discrétion totale et une sécurité financière maximale.
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 pt-2">
                                    <div className="bg-black/40 border border-purple-800/40 rounded-xl p-3.5 space-y-1">
                                        <div className="flex items-center gap-2 text-white font-bold text-xs">
                                            <ShieldIcon className="w-4 h-4 text-red-400" />
                                            <span>Zéro Libellé Bancaire</span>
                                        </div>
                                        <p className="text-[11px] text-purple-200/80 leading-relaxed">
                                            Aucune mention de voyance, de divination ou de notre cabinet n'apparaît sur vos relevés de compte. Votre achat de ticket ressemble à n'importe quel achat du quotidien.
                                        </p>
                                    </div>

                                    <div className="bg-black/40 border border-purple-800/40 rounded-xl p-3.5 space-y-1">
                                        <div className="flex items-center gap-2 text-white font-bold text-xs">
                                            <LockIcon className="w-4 h-4 text-emerald-400" />
                                            <span>Aucune Coordonnée Bancaire Transmise</span>
                                        </div>
                                        <p className="text-[11px] text-purple-200/80 leading-relaxed">
                                            Vous ne saisissez jamais votre numéro de carte de crédit ni cryptogramme sur notre site. Vos coordonnées restent 100% privées et protégées.
                                        </p>
                                    </div>

                                    <div className="bg-black/40 border border-purple-800/40 rounded-xl p-3.5 space-y-1">
                                        <div className="flex items-center gap-2 text-white font-bold text-xs">
                                            <CreditCardIcon className="w-4 h-4 text-amber-400" />
                                            <span>Paiement en Espèces Disponible</span>
                                        </div>
                                        <p className="text-[11px] text-purple-200/80 leading-relaxed">
                                            Vous pouvez acheter votre coupon Transcash en liquide chez votre buraliste pour une consultation totalement anonyme sans aucune trace bancaire.
                                        </p>
                                    </div>

                                    <div className="bg-black/40 border border-purple-800/40 rounded-xl p-3.5 space-y-1">
                                        <div className="flex items-center gap-2 text-white font-bold text-xs">
                                            <SparklesIcon className="w-4 h-4 text-gold-400" />
                                            <span>Maîtrise Totale du Budget (Sans Abonnement)</span>
                                        </div>
                                        <p className="text-[11px] text-purple-200/80 leading-relaxed">
                                            Pas de prélèvement automatique, pas de renouvellement caché. Vous ne payez que le montant exact de votre recharge et vos Astres sont acquis à vie.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-black/50 border border-red-500/30 rounded-2xl p-4 text-xs text-purple-200/90 flex flex-col sm:flex-row items-center justify-between gap-3">
                                <div className="flex items-center gap-2.5">
                                    <span className="text-amber-400 text-base shrink-0">ℹ</span>
                                    <span>Procurez-vous votre ticket Transcash (50€, 100€, 150€) et transmettez le code à notre secrétariat en direct pour activer vos Astres immédiatement.</span>
                                </div>
                                <button
                                    onClick={() => {
                                        const el = document.getElementById('pricing-grid');
                                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-red-600 via-rose-600 to-red-600 hover:from-red-500 hover:to-rose-500 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-red-950/60 shrink-0 transition-all cursor-pointer active:scale-95"
                                >
                                    Choisir mon Forfait →
                                </button>
                            </div>
                        </div>

                        {/* --- SECTION COMMENT FONCTIONNENT LES ASTRES ? (3 ÉTAPES) --- */}
                        <div className="max-w-4xl mx-auto mb-14 bg-[#12102A]/80 border border-purple-900/50 rounded-3xl p-6 sm:p-8 shadow-2xl">
                            <div className="text-center mb-8">
                                <h2 className="text-xl sm:text-2xl font-serif font-bold text-white mb-2">
                                    Comment fonctionnent les Astres ?
                                </h2>
                                <p className="text-xs text-purple-300/80">
                                    Un système simple, fluide et sans engagement pour vos consultations privées
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                                {/* Étape 01 */}
                                <div className="bg-black/40 border border-purple-900/40 rounded-2xl p-5 text-center space-y-2 relative">
                                    <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-gold-400 font-mono block">
                                        01
                                    </span>
                                    <h3 className="text-sm font-bold text-white">Choisissez votre forfait</h3>
                                    <p className="text-xs text-purple-200/80 leading-relaxed font-light">
                                        Sélectionnez le nombre d'Astres dont vous avez besoin selon vos envies.
                                    </p>
                                </div>

                                {/* Étape 02 */}
                                <div className="bg-black/40 border border-purple-900/40 rounded-2xl p-5 text-center space-y-2 relative">
                                    <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-amber-300 font-mono block">
                                        02
                                    </span>
                                    <h3 className="text-sm font-bold text-white">Recevez vos Astres</h3>
                                    <p className="text-xs text-purple-200/80 leading-relaxed font-light">
                                        Votre recharge est créditée selon le processus de paiement utilisé.
                                    </p>
                                </div>

                                {/* Étape 03 */}
                                <div className="bg-black/40 border border-purple-900/40 rounded-2xl p-5 text-center space-y-2 relative">
                                    <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400 font-mono block">
                                        03
                                    </span>
                                    <h3 className="text-sm font-bold text-white">Consultez votre médium</h3>
                                    <p className="text-xs text-purple-200/80 leading-relaxed font-light">
                                        Utilisez vos Astres pour vos consultations vocales directes sans attente.
                                    </p>
                                </div>
                            </div>

                            <div className="text-center p-3 bg-purple-950/40 border border-purple-800/40 rounded-2xl text-xs text-purple-200">
                                <span>Tarif indicatif : <strong>20 Astres / minute</strong> pour les consultations vocales concernées.</span>
                            </div>
                        </div>

                        {/* --- SECTION FAQ ACCORDION MODERNE --- */}
                        <div className="max-w-3xl mx-auto space-y-3">
                            <div className="text-center mb-6">
                                <h2 className="text-xl sm:text-2xl font-serif font-bold text-white mb-1">
                                    Foire Aux Questions
                                </h2>
                                <p className="text-xs text-purple-300/80">
                                    Tout ce que vous devez savoir sur vos forfaits et consultations
                                </p>
                            </div>

                            <div className="space-y-3">
                                {faqItems.map((item, idx) => {
                                    const isOpen = openFaqIndex === idx;

                                    return (
                                        <div
                                            key={idx}
                                            className="bg-[#12102A]/90 border border-purple-900/40 rounded-2xl overflow-hidden transition-all shadow-md"
                                        >
                                            <button
                                                onClick={() => toggleFaq(idx)}
                                                className="w-full p-4 text-left flex items-center justify-between gap-3 text-xs sm:text-sm font-bold text-white hover:text-amber-300 transition-colors cursor-pointer"
                                            >
                                                <span>{item.question}</span>
                                                <ChevronDownIcon
                                                    className={`w-4 h-4 text-purple-400 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180 text-amber-300' : ''}`}
                                                />
                                            </button>

                                            {isOpen && (
                                                <div className="px-4 pb-4 pt-1 text-xs text-purple-200/90 leading-relaxed border-t border-purple-900/30 animate-fade-in font-light">
                                                    {item.answer}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

            </div>
        </div>
    );
};

export default Services;
